/**
 * Tipo de gasto: a dimensão que o dono pediu em 22/09/2026 — "tem gastos que
 * são investimento e gasto que são 'luxos' pessoais".
 *
 * ⚠️⚠️ O chute NUNCA empurra uma categoria desconhecida para um balde. Uma
 * categoria não reconhecida vira `NAO_CLASSIFICADO`, que a tela Médias mostra
 * SEMPRE, mesmo zerada. Um chute silencioso e errado é pior que um vazio
 * visível: ele envenena a média sem deixar rastro — e a fila de Pendências,
 * que avisaria, saiu do menu nesta mesma leva.
 */

export type TipoDeGasto = 'essencial' | 'luxo' | 'investimento';

export const NAO_CLASSIFICADO = '__nao_classificado__';

export type ChaveDeTipo = TipoDeGasto | typeof NAO_CLASSIFICADO;

/**
 * ⭐ Revisão final da branch (22/09/2026) — o rótulo CANÔNICO do balde
 * `NAO_CLASSIFICADO`, pra parar de ter três nomes pro mesmo conceito
 * (`inicio/GastoMedioPorTipo.tsx` dizia "Sem tipo", `medias/BlocoDeTipo.tsx`
 * dizia "Não classificado"). Escolhido "Sem tipo": mais curto que "Não
 * classificado", cabe melhor no card de `GastoMedioPorTipo` em 390px de
 * largura (mesmo card que já leva `estreito` para não cortar em celular).
 * `ajustes/TiposDeGasto.tsx` já usava a mesma palavra na frase do cabeçalho
 * ("N categorias sem tipo") e não precisou mudar.
 */
export const ROTULO_SEM_TIPO = 'Sem tipo';

/** categoriaId → tipo. Só guarda o que foi decidido; o resto é chutado na hora. */
export type MapaDeTipos = Record<string, TipoDeGasto>;

/**
 * ⚠️ A chave TEM que existir na união `SyncedPrefs`
 * (packages/loot-core/src/types/prefs.ts) — sem isso o typecheck quebra em
 * `useSyncedPref`.
 */
export const CHAVE_PREF = 'finapp-tipos-de-gasto' as const;

const SEMENTES: Record<TipoDeGasto, string[]> = {
  essencial: [
    'aluguel',
    'condominio',
    'energia',
    'agua',
    'luz',
    'conta de gas',
    'internet',
    'telefone',
    'celular',
    'mercado',
    'supermercado',
    'farmacia',
    'saude',
    'plano de saude',
    'escola',
    'faculdade',
    'transporte',
    'combustivel',
    'gasolina',
    'financiamento',
    'emprestimo',
    'seguro',
    'imposto',
    'iptu',
    'ipva',
  ],
  investimento: [
    'aporte',
    'investimento',
    'aplicacao',
    'poupanca',
    'reserva',
    'previdencia',
    'tesouro',
    'acoes',
    'fundo',
    'cdb',
    'renda fixa',
  ],
  luxo: [
    'restaurante',
    'bar',
    'delivery',
    'ifood',
    'viagem',
    'hotel',
    'streaming',
    'assinatura',
    'lazer',
    'presente',
    'roupa',
    'beleza',
    'jogos',
    'cinema',
    'uber',
  ],
};

/** Minúsculas, sem acento — `'Condomínio'` e `'CONDOMINIO'` batem na mesma semente. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * ⚠️ A ordem é fixa para o resultado ser estável e previsível: se um nome
 * casar com sementes de mais de um tipo, ganha o primeiro tipo desta lista.
 * Isso importa se as listas crescerem e passarem a se sobrepor.
 */
const ORDEM: TipoDeGasto[] = ['investimento', 'essencial', 'luxo'];

export function chutarTipo(nomeDaCategoria: string): TipoDeGasto | null {
  const nome = normalizar(nomeDaCategoria);
  if (nome === '') return null;

  for (const tipo of ORDEM) {
    if (SEMENTES[tipo].some(semente => nome.includes(semente))) {
      return tipo;
    }
  }
  return null;
}

/**
 * A decisão do dono (no mapa) SEMPRE vence o chute. Só cai no chute quando a
 * categoria nunca foi classificada à mão.
 */
export function tipoDaCategoria(
  categoriaId: string | null,
  nomes: Map<string, string>,
  mapa: MapaDeTipos,
): ChaveDeTipo {
  if (categoriaId === null) return NAO_CLASSIFICADO;

  const decidido = mapa[categoriaId];
  if (decidido) return decidido;

  const nome = nomes.get(categoriaId);
  if (!nome) return NAO_CLASSIFICADO;

  return chutarTipo(nome) ?? NAO_CLASSIFICADO;
}

/**
 * ⚠️ Tolerante a lixo de propósito: a pref é um texto livre que já passou por
 * sincronização entre dispositivos. JSON quebrado, `null`, ou um valor que não
 * é um dos três tipos viram "nada decidido" — a tela cai no chute e continua
 * funcionando. Jogar exceção aqui derrubaria a tela inteira por causa de um
 * caractere.
 */
export function lerMapa(bruto: string | undefined): MapaDeTipos {
  if (!bruto) return {};

  try {
    const cru: unknown = JSON.parse(bruto);
    if (typeof cru !== 'object' || cru === null || Array.isArray(cru)) {
      return {};
    }

    const valido: MapaDeTipos = {};
    for (const [id, tipo] of Object.entries(cru as Record<string, unknown>)) {
      if (tipo === 'essencial' || tipo === 'luxo' || tipo === 'investimento') {
        valido[id] = tipo;
      }
    }
    return valido;
  } catch {
    return {};
  }
}

export function gravarMapa(mapa: MapaDeTipos): string {
  return JSON.stringify(mapa);
}
